import { useCallback, useEffect, useState } from 'react';
import { apiFetch, resolveMediaUrl } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import {
  MicroPromotionDefaultMomentsImages,
  type DefaultMomentsImageItem,
} from '@/components/MicroPromotionDefaultMomentsImages';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { inputClass, labelClass } from '@/components/ui/inputStyles';
import type { MicroPromoInfoType } from '@/components/MicroPromotionParamsPanel';

const FREQ_OPTIONS = [
  { value: 'daily1', label: '每天 1 次' },
  { value: 'daily2', label: '每天 2 次' },
  { value: 'every2', label: '每 2 天 1 次' },
  { value: 'every3', label: '每 3 天 1 次' },
  { value: 'weekly', label: '每周 1 次' },
] as const;

type FreqKey = (typeof FREQ_OPTIONS)[number]['value'];

export type TaskWebhookItem = {
  id: number;
  name: string;
  enabled: boolean;
  /** 调试模式：Webhook 入库任务默认为「已暂停」，关闭则为「推广中」 */
  debug_mode: boolean;
  promotion_channel: 'all' | 'moments' | 'groups';
  moments_promotion_days: number;
  groups_promotion_days: number;
  entry_moments_freq: FreqKey;
  entry_groups_freq: FreqKey;
  entry_daily2_interval_hours: number;
  default_moments_image_paths?: string[];
  default_moments_images?: Array<{ path: string; preview_url?: string; url?: string }>;
  receive_url: string;
  webhook_secret: string;
};

type Props = {
  areaId: number;
  infoType: MicroPromoInfoType;
};

function channelLabel(ch: string): string {
  if (ch === 'moments') return '仅发圈';
  if (ch === 'groups') return '仅群发';
  return '发圈+群发';
}

function freqLabel(v: string): string {
  return FREQ_OPTIONS.find((o) => o.value === v)?.label || v;
}

function toDefaultMomentsImages(item: TaskWebhookItem | null): DefaultMomentsImageItem[] {
  if (!item) return [];
  const fromMeta = item.default_moments_images || [];
  if (fromMeta.length > 0) {
    return fromMeta.map((im) => ({
      path: im.path,
      url: im.preview_url || im.url || resolveMediaUrl(im.path),
    }));
  }
  return (item.default_moments_image_paths || []).map((path) => ({
    path,
    url: resolveMediaUrl(path),
  }));
}

function WebhookFormModal({
  open,
  areaId,
  infoType,
  item,
  onClose,
  onSaved,
}: {
  open: boolean;
  areaId: number;
  infoType: MicroPromoInfoType;
  item: TaskWebhookItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [name, setName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [debugMode, setDebugMode] = useState(false);
  const [channel, setChannel] = useState<'all' | 'moments' | 'groups'>('all');
  const [momentsDays, setMomentsDays] = useState(7);
  const [groupsDays, setGroupsDays] = useState(7);
  const [momentsFreq, setMomentsFreq] = useState<FreqKey>('daily1');
  const [groupsFreq, setGroupsFreq] = useState<FreqKey>('every2');
  const [daily2Hours, setDaily2Hours] = useState(6);
  const [momentsImages, setMomentsImages] = useState<DefaultMomentsImageItem[]>([]);

  useEffect(() => {
    if (!open) return;
    setMsg('');
    if (item) {
      setName(item.name || '');
      setEnabled(item.enabled);
      setDebugMode(!!item.debug_mode);
      setChannel(item.promotion_channel || 'all');
      setMomentsDays(item.moments_promotion_days || 7);
      setGroupsDays(item.groups_promotion_days || 7);
      setMomentsFreq(item.entry_moments_freq || 'daily1');
      setGroupsFreq(item.entry_groups_freq || 'every2');
      setDaily2Hours(item.entry_daily2_interval_hours || 6);
      setMomentsImages(toDefaultMomentsImages(item));
    } else {
      setName('');
      setEnabled(true);
      setDebugMode(false);
      setChannel('all');
      setMomentsDays(7);
      setGroupsDays(7);
      setMomentsFreq('daily1');
      setGroupsFreq('every2');
      setDaily2Hours(6);
      setMomentsImages([]);
    }
  }, [open, item]);

  async function save() {
    setSaving(true);
    setMsg('');
    try {
      const trimmedName = name.trim();
      if (!trimmedName) {
        setMsg('请填写名称');
        return;
      }
      const needsImages = channel === 'all' || channel === 'moments';
      if (needsImages && momentsImages.length === 0) {
        setMsg('发圈推广需至少上传一张默认配图（微信群推广为纯文字）');
        return;
      }
      await apiFetch('save_micro_promotion_task_webhook', {
        method: 'POST',
        body: JSON.stringify({
          id: item?.id || 0,
          area_id: areaId,
          info_type: infoType,
          name: trimmedName,
          enabled,
          debug_mode: debugMode,
          promotion_channel: channel,
          moments_promotion_days: momentsDays,
          groups_promotion_days: groupsDays,
          entry_moments_freq: momentsFreq,
          entry_groups_freq: groupsFreq,
          entry_daily2_interval_hours: daily2Hours,
          default_moments_image_paths: momentsImages.map((im) => im.path),
        }),
      });
      onSaved();
      onClose();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  }

  const showMoments = channel === 'all' || channel === 'moments';
  const showGroups = channel === 'all' || channel === 'groups';

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title={item ? `编辑 Webhook 接收 #${item.id}` : '添加 Webhook 任务接收'}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
            取消
          </Button>
          <Button type="button" variant="primary" disabled={saving} onClick={() => void save()}>
            {saving ? '保存中…' : '保存'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-slate-500">
          外部系统审核通过后 POST 推广文案到此地址，将自动创建图文推广任务。微信群为纯文字；朋友圈使用下方默认配图。
          机器人转发朋友圈 / 微信群时，文字仅使用入库的「推广文案」原文，不套用区域「参数配置」里的转发模板（不拼接电话、不截断）。
        </p>
        <label className="block text-sm">
          <span className={labelClass}>名称</span>
          <input
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="例如：外部系统名称"
            maxLength={128}
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          启用
        </label>
        <div className="rounded-xl border border-amber-100 bg-amber-50/60 px-3 py-2.5 space-y-1.5">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={debugMode} onChange={(e) => setDebugMode(e.target.checked)} />
            <span className="font-medium text-slate-800">调试模式</span>
          </label>
          <p className="text-xs text-slate-600 leading-relaxed pl-6">
            开启后，Webhook 写入「图文推广列表」的任务默认为「已暂停」，便于核对文案与配图后再手动改为「推广中」；关闭后入库即为「推广中」。
            （若命中敏感词仍会进入待审核，不受此开关影响。）
          </p>
        </div>
        <label className="block text-sm">
          <span className={labelClass}>推广渠道</span>
          <select className={inputClass} value={channel} onChange={(e) => setChannel(e.target.value as typeof channel)}>
            <option value="all">发圈 + 群发</option>
            <option value="moments">仅发圈</option>
            <option value="groups">仅群发（纯文字）</option>
          </select>
        </label>
        {showMoments ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className={labelClass}>发圈推广天数</span>
              <input type="number" min={1} max={180} className={inputClass} value={momentsDays} onChange={(e) => setMomentsDays(Number(e.target.value))} />
            </label>
            <label className="block text-sm">
              <span className={labelClass}>发圈转发频次</span>
              <select className={inputClass} value={momentsFreq} onChange={(e) => setMomentsFreq(e.target.value as FreqKey)}>
                {FREQ_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
          </div>
        ) : null}
        {showGroups ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className={labelClass}>群发推广天数</span>
              <input type="number" min={1} max={180} className={inputClass} value={groupsDays} onChange={(e) => setGroupsDays(Number(e.target.value))} />
            </label>
            <label className="block text-sm">
              <span className={labelClass}>群发转发频次</span>
              <select className={inputClass} value={groupsFreq} onChange={(e) => setGroupsFreq(e.target.value as FreqKey)}>
                {FREQ_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
          </div>
        ) : null}
        {(momentsFreq === 'daily2' || groupsFreq === 'daily2') ? (
          <label className="block text-sm">
            <span className={labelClass}>每天 2 次间隔（小时）</span>
            <input type="number" min={1} max={24} className={inputClass} value={daily2Hours} onChange={(e) => setDaily2Hours(Number(e.target.value))} />
          </label>
        ) : null}
        {showMoments ? (
          <div>
            <span className={labelClass}>发圈默认配图</span>
            <div className="mt-2">
              <MicroPromotionDefaultMomentsImages
                areaId={areaId}
                infoType={infoType}
                items={momentsImages}
                onChange={setMomentsImages}
                disabled={saving}
              />
            </div>
          </div>
        ) : null}
        {item?.receive_url ? (
          <div className="rounded-xl border bg-slate-50 p-3 text-xs">
            <div className="font-medium text-slate-700 mb-1">接收地址（复制到外部系统的 Webhook 推送 URL）</div>
            <code className="block break-all text-teal-800">{item.receive_url}</code>
          </div>
        ) : null}
        {msg ? <p className="text-sm text-rose-600">{msg}</p> : null}
      </div>
    </Modal>
  );
}

export function MicroPromotionTaskWebhookPanel({ areaId, infoType }: Props) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<TaskWebhookItem[]>([]);
  const [msg, setMsg] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<TaskWebhookItem | null>(null);
  const [busyId, setBusyId] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch<{ ok: boolean; items: TaskWebhookItem[] }>('list_micro_promotion_task_webhooks', {
        query: { area_id: areaId, info_type: infoType },
      });
      setItems(r.items || []);
    } finally {
      setLoading(false);
    }
  }, [areaId, infoType]);

  useEffect(() => {
    if (infoType === 'graphic') void load();
  }, [infoType, load]);

  if (infoType !== 'graphic') return null;

  async function copyText(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setMsg('已复制');
    } catch {
      setMsg('复制失败');
    }
  }

  async function remove(id: number) {
    if (!window.confirm('确定删除此 Webhook 接收配置？')) return;
    setBusyId(id);
    try {
      await apiFetch('delete_micro_promotion_task_webhook', {
        method: 'POST',
        body: JSON.stringify({ id, area_id: areaId }),
      });
      await load();
    } finally {
      setBusyId(0);
    }
  }

  return (
    <>
    <Card title="Webhook 任务接收">
      <p className="text-xs text-slate-500 mb-3">
        可配置多个接收端，每个拥有独立的推广天数、频次、渠道、发圈配图与调试模式。外部系统推送 <code className="text-teal-700">promotion_text</code> 即可创建任务。
      </p>
      <div className="mb-3">
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            setEditItem(null);
            setModalOpen(true);
          }}
        >
          添加 Webhook 接收
        </Button>
      </div>
      {msg ? <p className="text-sm text-emerald-700 mb-2">{msg}</p> : null}
      {loading ? (
        <Spinner label="加载 Webhook 配置" />
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-500">尚未配置 Webhook 接收。添加后可将接收 URL 填入外部系统后台。</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((it) => (
            <li key={it.id} className="py-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{it.name || `Webhook #${it.id}`}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${it.enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  {it.enabled ? '启用' : '停用'}
                </span>
                {it.debug_mode ? (
                  <span className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-800">调试·入库暂停</span>
                ) : null}
                <span className="text-xs text-slate-500">{channelLabel(it.promotion_channel)}</span>
              </div>
              <div className="text-xs text-slate-600">
                发圈 {it.moments_promotion_days} 天 · {freqLabel(it.entry_moments_freq)}
                {' · '}
                群发 {it.groups_promotion_days} 天 · {freqLabel(it.entry_groups_freq)}
              </div>
              <code className="block text-[11px] break-all text-teal-800 bg-slate-50 rounded px-2 py-1">{it.receive_url}</code>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="secondary" className="!px-2 !py-1 text-xs" onClick={() => void copyText(it.receive_url)}>复制 URL</Button>
                <Button type="button" variant="secondary" className="!px-2 !py-1 text-xs" onClick={() => { setEditItem(it); setModalOpen(true); }}>编辑</Button>
                <Button type="button" variant="ghost" className="!px-2 !py-1 text-xs" disabled={busyId === it.id} onClick={() => void remove(it.id)}>删除</Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
    <WebhookFormModal
      open={modalOpen}
      areaId={areaId}
      infoType={infoType}
      item={editItem}
      onClose={() => { setModalOpen(false); setEditItem(null); }}
      onSaved={() => void load()}
    />
    </>
  );
}
